import { useMemo } from "react";
import { useQuery } from "@tanstack/react-query";
import { BarChart3, Layers3, RefreshCw, Users2 } from "lucide-react";

import EmptyState from "@/components/layout/EmptyState";
import MetricCard from "@/components/layout/MetricCard";
import PageHeader from "@/components/layout/PageHeader";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { api } from "@/lib/api";
import { formatRenderCollectionLabel } from "@/lib/render-catalog";

const numberFormatter = new Intl.NumberFormat("vi-VN");

function RankedList({
  title,
  description,
  items,
}: {
  title: string;
  description: string;
  items: Array<{ label: string; value: number }>;
}) {
  const maxValue = items.reduce((current, item) => Math.max(current, item.value), 0);

  return (
    <Card className="border-border/80 bg-card/80">
      <CardHeader>
        <CardTitle>{title}</CardTitle>
        <CardDescription>{description}</CardDescription>
      </CardHeader>
      <CardContent className="space-y-3">
        {items.length === 0 ? (
          <EmptyState
            title="Chưa có dữ liệu"
            description="Analytics breakdown sẽ hiện khi catalog đã có bản ghi đủ để tổng hợp."
            className="p-6"
          />
        ) : (
          items.map((item) => {
            const width = maxValue > 0 ? Math.max((item.value / maxValue) * 100, 8) : 0;
            return (
              <div key={item.label} className="space-y-2 rounded-2xl border border-border/80 bg-muted/20 p-3">
                <div className="flex items-center justify-between gap-3 text-sm">
                  <span className="truncate font-medium">{item.label}</span>
                  <span className="text-muted-foreground">
                    {numberFormatter.format(item.value)}
                  </span>
                </div>
                <div className="h-2 rounded-full bg-muted">
                  <div className="h-2 rounded-full bg-primary/80" style={{ width: `${width}%` }} />
                </div>
              </div>
            );
          })
        )}
      </CardContent>
    </Card>
  );
}

export default function AnalyticsPage() {
  const statsQuery = useQuery({
    queryKey: ["stats-overview"],
    queryFn: api.getStats,
  });

  const topCollections = useMemo(
    () =>
      Object.entries(statsQuery.data?.collectionCounts ?? {})
        .map(([label, value]) => ({
          label: formatRenderCollectionLabel(label),
          value,
        }))
        .sort((left, right) => right.value - left.value || left.label.localeCompare(right.label))
        .slice(0, 10),
    [statsQuery.data?.collectionCounts],
  );

  const topComponents = useMemo(
    () =>
      Object.entries(statsQuery.data?.componentCounts ?? {})
        .map(([label, value]) => ({ label, value }))
        .sort((left, right) => right.value - left.value || left.label.localeCompare(right.label))
        .slice(0, 10),
    [statsQuery.data?.componentCounts],
  );

  if (statsQuery.isLoading) {
    return <div className="text-sm text-muted-foreground">Loading analytics...</div>;
  }

  if (statsQuery.isError || !statsQuery.data) {
    return <div className="text-sm text-red-500">Không tải được analytics.</div>;
  }

  const stats = statsQuery.data;
  const totalGender = (stats.genderCounts.male || 0) + (stats.genderCounts.female || 0);
  const pricedCoverage = stats.totalAssets > 0 ? (stats.pricedAssets / stats.totalAssets) * 100 : 0;

  return (
    <div className="space-y-6">
      <PageHeader
        eyebrow="Breakdowns"
        title="Analytics"
        description="Tổng hợp nhanh theo gender, pricing coverage và các nhóm đang chiếm tỷ trọng lớn nhất."
        actions={
          <Button
            variant="outline"
            onClick={() => {
              void statsQuery.refetch();
            }}
            disabled={statsQuery.isFetching}
          >
            <RefreshCw className={`mr-2 h-4 w-4 ${statsQuery.isFetching ? "animate-spin" : ""}`} />
            Reload
          </Button>
        }
      />

      <div className="grid gap-4 md:grid-cols-3">
        <MetricCard
          label="Gender split"
          value={`${numberFormatter.format(stats.genderCounts.male || 0)} / ${numberFormatter.format(stats.genderCounts.female || 0)}`}
          hint={totalGender > 0 ? `Male ${(stats.genderCounts.male / totalGender * 100).toFixed(1)}% • Female ${(stats.genderCounts.female / totalGender * 100).toFixed(1)}%` : "Chưa có dữ liệu gender."}
          icon={<Users2 className="h-4 w-4" />}
        />
        <MetricCard
          label="Pricing coverage"
          value={`${pricedCoverage.toFixed(pricedCoverage >= 100 ? 0 : 1)}%`}
          hint={`${numberFormatter.format(stats.pricedAssets)} priced • ${numberFormatter.format(stats.unpricedAssets)} pending`}
          icon={<BarChart3 className="h-4 w-4" />}
        />
        <MetricCard
          label="Collection density"
          value={numberFormatter.format(stats.collections)}
          hint={`${numberFormatter.format(topCollections[0]?.value || 0)} asset nằm ở collection lớn nhất hiện tại.`}
          icon={<Layers3 className="h-4 w-4" />}
        />
      </div>

      <div className="grid gap-6 xl:grid-cols-2">
        <RankedList
          title="Top collections"
          description="Collection ranking theo số asset hiện có trong catalog."
          items={topCollections}
        />
        <RankedList
          title="Top components"
          description="Component/prop buckets có tần suất xuất hiện cao nhất."
          items={topComponents}
        />
      </div>
    </div>
  );
}
